"use client"
import { Button, Typography } from "@mui/material"
import { visuallyHidden } from "@mui/utils"
import { CarModelFinishingLevelsDto } from "api"
import { useIsDesktop } from "common/MediaQueries"
import { setDetail } from "components/shopFeatures/detailing/detailingSlice"
import Image from "next/image"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { Suspense, useEffect, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import BigChipsOneRow from "reusable/chips/BigChipsOneRow"
import MultibleChipsRow from "reusable/chips/MultibleChipsRow"
import styled from "styled-components"
import { StyledCol, TypographyCenter } from "theme/shared"

import BulletsList from "./BulletsList"
import FinishingLevelsPopup from "./FinishingLevelsPopup"
const StyledSectionWrapper = styled.div`
  min-height: 90vh;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${(p) => p.theme.colors.lightGrey};
  justify-content: center;
`

const StyledLicenseFee = styled(Typography).attrs({ variant: "body2" })`
  font-size: 12px;
  color: ${(p) => p.theme.colors.disable};
  text-align: center;
  margin: 0 auto;
`
const StyledCubicWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  padding-bottom: 80px;
  ${(p) => p.theme.breakpoints.up("md")} {
    flex-direction: row;
    align-items: stretch;
  }
`
const StyledFramedDiv = styled.div`
  width: 300px;
  max-width: 300px;
  display: flex;
  flex-direction: column;
  padding: 10px;
  border: 1px solid ${(p) => p.theme.colors.platinum};
  border-radius: 3px;
  min-height: 220px;
`
const StyledGrow = styled.div<{ $start?: boolean }>`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: ${(p) => (p.$start ? "flex-start" : "center")};
  align-items: ${(p) => (p.$start ? "flex-start" : "center")};
`
const StyledButton = styled(Button)`
  margin-top: 10px;
  margin: 0 auto;
`
const StyledImageWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`
type FinishingLevelsSectionProps = {
  data: CarModelFinishingLevelsDto
  modelName: string
  setCalculatorPopupOpen?: (falsy: boolean) => void
}

const FinishingLevelsSectionContent = ({
  data,
  modelName,
  setCalculatorPopupOpen,
}: FinishingLevelsSectionProps) => {
  const isDesktop = useIsDesktop()
  const [popUpOpen, setPopUpOpen] = useState(false)
  const [selectedEngineIndex, setSelectedEngineIndex] = useState(0)
  const [selectedFinishingLevelsIndex, setSelectedFinishingLevelsIndex] =
    useState(0)
  const dispatch = useDispatch()

  const enginesOptions = useMemo(() => {
    return data?.engines.map((item) => item.engineName)
  }, [data?.engines])

  const finishingLevelsOptions = useMemo(() => {
    return data?.engines[selectedEngineIndex].finishingLevels.map(
      (item) => item.name
    )
  }, [selectedEngineIndex, data?.engines])

  const selectedData = useMemo(() => {
    return data?.engines[selectedEngineIndex].finishingLevels[
      selectedFinishingLevelsIndex
    ]
  }, [selectedEngineIndex, selectedFinishingLevelsIndex, data?.engines])

  useEffect(() => {
    if (selectedData?.code) {
      dispatch(setDetail(selectedData.code))
    }
  }, [dispatch, selectedData?.code])

  useEffect(() => {
    setSelectedFinishingLevelsIndex(0)
  }, [selectedEngineIndex])

  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const onClick = () => {
    // clone existing params
    const params = new URLSearchParams(searchParams.toString())

    // set / update your params
    if (selectedData?.name) params.set("finishingLevel", selectedData.name)
    const engineId = data?.engines?.[selectedEngineIndex]?.engineId
    if (engineId != null) params.set("engineId", String(engineId))

    // keep current hash (client-only)
    const hash = typeof window !== "undefined" ? window.location.hash : ""

    const url = `${pathname}?${params.toString()}${hash}`

    router.replace(url, { scroll: false }) // App Router option
    setCalculatorPopupOpen?.(true)
  }

  return (
    <>
      <StyledSectionWrapper>
        <TypographyCenter variant="h2" $mb="20px">
          רמות גימור
        </TypographyCenter>
        <Typography sx={visuallyHidden}>
          הצג נתונים על כל דגם , על פי המאפיינים הבאים לבחירתך
        </Typography>
        <StyledCol $fullWidth $gap="4px" $pr={"16px"} $pl={"16px"}>
          {enginesOptions?.length > 1 && (
            <BigChipsOneRow
              activeButton={selectedEngineIndex}
              setActiveButton={setSelectedEngineIndex}
              onSelect={() => setSelectedFinishingLevelsIndex(0)}
              optionsList={enginesOptions}
              toggleId="engine-selection"
            />
          )}
          <MultibleChipsRow
            activeButton={selectedFinishingLevelsIndex}
            setActiveButton={setSelectedFinishingLevelsIndex}
            optionsList={finishingLevelsOptions}
            toggleId={`finishing-levels-selection`}
          />
        </StyledCol>

        <StyledImageWrapper>
          <Image
            src={selectedData.image.imageUrl}
            alt={selectedData.image.alt || ""}
            width={isDesktop ? 544 : 320}
            height={isDesktop ? 306 : 180}
            style={{
              maxWidth: "100%",
              height: "auto",
            }}
          />
        </StyledImageWrapper>
        <StyledCubicWrapper>
          {/* מפרט: */}
          <StyledFramedDiv tabIndex={0}>
            <StyledGrow $start>
              <BulletsList textList={selectedData.mainFeatures} />
            </StyledGrow>
            <StyledButton
              variant="contained"
              onClick={() => setPopUpOpen(true)}
            >
              למפרט המלא
            </StyledButton>
          </StyledFramedDiv>
          {/* מחיר */}
          <StyledFramedDiv tabIndex={0}>
            <StyledGrow>
              <TypographyCenter
                variant="body1"
                $fontSize="24px"
                $fontWeight="400"
              >
                {`₪${selectedData.price.toLocaleString()}`}
              </TypographyCenter>
              {selectedData.licenceFee > 0 && (
                <StyledLicenseFee>
                  בתוספת אגרת רישוי בסך ₪
                  {selectedData.licenceFee.toLocaleString()} כולל מע״מ
                </StyledLicenseFee>
              )}
            </StyledGrow>
            <StyledButton onClick={onClick} variant="contained">
              למחשבון מימון
            </StyledButton>
          </StyledFramedDiv>
        </StyledCubicWrapper>
      </StyledSectionWrapper>
      {/* popups: */}
      <FinishingLevelsPopup
        open={popUpOpen}
        onClose={() => setPopUpOpen(false)}
        modelName={modelName}
        data={selectedData}
      />
    </>
  )
}

const FinishingLevelsSection = (props: FinishingLevelsSectionProps) => (
  <Suspense fallback={<div>Loading...</div>}>
    <FinishingLevelsSectionContent {...props} />
  </Suspense>
)

export default FinishingLevelsSection
